import React, { useEffect, useState } from 'react';
import Modal from './ModalTrue';
import { DuplicationAudit } from '../api/claims';

import './DuplicationAuditModal.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  existing: DuplicationAudit | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  claimFactsAutoSummary: string;
  onSave: (
    data: Omit<DuplicationAudit, 'completedBy' | 'completedAt' | 'lastUpdatedBy' | 'lastUpdatedAt'>
  ) => Promise<void>;
};

type FormState = {
  hasInstructedOtherFirm: 'yes' | 'no' | 'not_sure' | '';
  otherFirmNames: string;
  firstInstructionDate: string;
  hasCancelledFirm: 'yes' | 'no' | '';
  cancelledFirmDetails: string;
  claimFactsSummary: string;
  claimFactsConfirmed: boolean;
  notes: string;
};

const blankForm = (claimFactsAutoSummary: string): FormState => ({
  hasInstructedOtherFirm: '',
  otherFirmNames: '',
  firstInstructionDate: '',
  hasCancelledFirm: '',
  cancelledFirmDetails: '',
  claimFactsSummary: claimFactsAutoSummary,
  claimFactsConfirmed: false,
  notes: '',
});

const existingToForm = (existing: DuplicationAudit, fallbackSummary: string): FormState => ({
  hasInstructedOtherFirm: existing.hasInstructedOtherFirm,
  otherFirmNames: existing.otherFirmNames ?? '',
  firstInstructionDate: existing.firstInstructionDate ?? '',
  hasCancelledFirm: existing.hasCancelledFirm ?? '',
  cancelledFirmDetails: existing.cancelledFirmDetails ?? '',
  claimFactsSummary: existing.claimFactsSummary ?? fallbackSummary,
  claimFactsConfirmed: existing.claimFactsConfirmed,
  notes: existing.notes ?? '',
});

const DuplicationAuditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  existing,
  loading,
  saving,
  error,
  claimFactsAutoSummary,
  onSave,
}) => {
  const [form, setForm] = useState<FormState>(blankForm(claimFactsAutoSummary));

  // Reset form when modal opens or when existing data arrives
  useEffect(() => {
    if (!isOpen) return;

    if (existing) {
      setForm(existingToForm(existing, claimFactsAutoSummary));
    } else {
      setForm(blankForm(claimFactsAutoSummary));
    }
  }, [isOpen, existing, claimFactsAutoSummary]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-active');
    } else {
      document.body.classList.remove('modal-active');
    }
    return () => {
      document.body.classList.remove('modal-active');
    };
  }, [isOpen]);

  const handleChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const canSave = form.hasInstructedOtherFirm !== '' && !saving;

  const handleSave = async () => {
    if (!canSave) return;

    const payload: Omit<DuplicationAudit, 'completedBy' | 'completedAt' | 'lastUpdatedBy' | 'lastUpdatedAt'> = {
      hasInstructedOtherFirm: form.hasInstructedOtherFirm as 'yes' | 'no' | 'not_sure',
      claimFactsConfirmed: form.claimFactsConfirmed,
      claimFactsSummary: form.claimFactsSummary || undefined,
      notes: form.notes || undefined,
    };

    // Only include conditional fields when answer is "yes"
    if (form.hasInstructedOtherFirm === 'yes') {
      payload.otherFirmNames = form.otherFirmNames || undefined;
      payload.firstInstructionDate = form.firstInstructionDate || undefined;
      payload.hasCancelledFirm = form.hasCancelledFirm === '' ? null : (form.hasCancelledFirm as 'yes' | 'no');
      if (form.hasCancelledFirm === 'yes') {
        payload.cancelledFirmDetails = form.cancelledFirmDetails || undefined;
      }
    }

    await onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <div className="dup-audit">
        {/* Header */}
        <div className="dup-audit__header">
          <h2>{existing ? 'Edit Duplication Audit' : 'Duplication Audit'}</h2>
          {existing && (existing.completedBy || existing.completedAt || existing.lastUpdatedBy || existing.lastUpdatedAt) && (
            <div className="dup-audit__meta">
              {existing.completedBy && (
                <span>
                  Completed by <strong>{existing.completedBy}</strong>
                  {existing.completedAt ? ` on ${new Date(existing.completedAt).toLocaleString()}` : ''}
                </span>
              )}
              {existing.lastUpdatedBy && (
                <span>
                  Last updated by <strong>{existing.lastUpdatedBy}</strong>
                  {existing.lastUpdatedAt ? ` on ${new Date(existing.lastUpdatedAt).toLocaleString()}` : ''}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="dup-audit__loading">Loading duplication audit data...</div>
        )}

        {/* Error banner */}
        {error && <div className="dup-audit__error">{error}</div>}

        {/* Form */}
        {!loading && (
          <div className="dup-audit__body">
            {/* Q1: Instructed other firm? */}
            <div className="dup-audit__question">
              <span className="dup-audit__label">
                Have you signed an agreement / instructed any other law firm, claims management
                company (&quot;CMC&quot;), or submitted a complaint to the lender yourself, in
                relation to this same claim?
              </span>
              <div className="dup-audit__radios">
                {([
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'not_sure', label: 'Not sure' },
                ] as const).map((opt) => (
                  <label key={opt.value} className="dup-audit__radio">
                    <input
                      type="radio"
                      name="hasInstructedOtherFirm"
                      value={opt.value}
                      checked={form.hasInstructedOtherFirm === opt.value}
                      onChange={() => handleChange('hasInstructedOtherFirm', opt.value)}
                      disabled={saving}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Conditional Q2-Q4: only when "yes" */}
            {form.hasInstructedOtherFirm === 'yes' && (
              <div className="dup-audit__conditional">
                {/* Q2: Which firm(s)? */}
                <div className="dup-audit__question">
                  <span className="dup-audit__label">
                    Which firm(s) / submitted themselves?
                  </span>
                  <input
                    type="text"
                    className="dup-audit__input"
                    placeholder="e.g. Slater & Gordon, or 'submitted themselves'"
                    value={form.otherFirmNames}
                    onChange={(e) => handleChange('otherFirmNames', e.target.value)}
                    disabled={saving}
                  />
                </div>

                {/* Q3: When? */}
                <div className="dup-audit__question">
                  <span className="dup-audit__label">
                    When did you first instruct that other firm or submit the complaint yourself?
                  </span>
                  <input
                    type="text"
                    className="dup-audit__input"
                    placeholder="e.g. March 2024 or 15/03/2024"
                    value={form.firstInstructionDate}
                    onChange={(e) => handleChange('firstInstructionDate', e.target.value)}
                    disabled={saving}
                  />
                </div>

                {/* Q4: Cancelled any firm? */}
                <div className="dup-audit__question">
                  <span className="dup-audit__label">Have you cancelled any firm?</span>
                  <div className="dup-audit__radios">
                    {([
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ] as const).map((opt) => (
                      <label key={opt.value} className="dup-audit__radio">
                        <input
                          type="radio"
                          name="hasCancelledFirm"
                          value={opt.value}
                          checked={form.hasCancelledFirm === opt.value}
                          onChange={() => handleChange('hasCancelledFirm', opt.value)}
                          disabled={saving}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {form.hasCancelledFirm === 'yes' && (
                    <input
                      type="text"
                      className="dup-audit__input"
                      placeholder="Which firm and when?"
                      value={form.cancelledFirmDetails}
                      onChange={(e) => handleChange('cancelledFirmDetails', e.target.value)}
                      disabled={saving}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Q5: Confirm claim facts */}
            <div className="dup-audit__question">
              <span className="dup-audit__label">
                Confirm this is the same defendant / claim facts
              </span>
              <div className="dup-audit__claim-facts">
                <textarea
                  className="dup-audit__textarea"
                  value={form.claimFactsSummary}
                  onChange={(e) => handleChange('claimFactsSummary', e.target.value)}
                  disabled={saving}
                />
                <label className="dup-audit__checkbox">
                  <input
                    type="checkbox"
                    checked={form.claimFactsConfirmed}
                    onChange={(e) => handleChange('claimFactsConfirmed', e.target.checked)}
                    disabled={saving}
                  />
                  Client confirms these are the same claim facts
                </label>
              </div>
            </div>

            {/* Q6: Notes */}
            <div className="dup-audit__question">
              <span className="dup-audit__label">Notes</span>
              <textarea
                className="dup-audit__textarea"
                placeholder="Any additional notes..."
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="dup-audit__footer">
            <button
              type="button"
              className="dup-audit__btn dup-audit__btn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="dup-audit__btn dup-audit__btn--primary"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? 'Saving...' : existing ? 'Update' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DuplicationAuditModal;
